import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import type { SaRole, SaUserDto } from '@sellokart/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthContext';
import { Alert } from '@/components/Alert';
import { AsyncSection } from '@/components/AsyncSection';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { SelectField, TextField } from '@/components/Form';
import { IconKey, IconPlus } from '@/components/Icons';
import { Modal } from '@/components/Modal';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { Skeleton } from '@/components/States';
import {
  createSaUser,
  fetchSaUsers,
  resetSaUserPassword,
  saUserKeys,
  updateSaUser,
} from '@/features/sa-users/api';
import { getErrorMessage } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { compactErrors, emailError, usernameError } from '@/lib/validate';

/** As the API requires — the same minimum as portal logins. */
const MIN_CONSOLE_PASSWORD = 8;

const ROLE_LABEL: Record<SaRole, string> = {
  OWNER: 'Owner',
  ISSUER: 'Issuer',
};

const ROLE_HINT: Record<SaRole, string> = {
  OWNER: 'The whole console.',
  ISSUER: 'Only creates tenants, adds tills and issues licences — and only sees the tenants it created.',
};

function consolePasswordError(value: string, label = 'Password'): string | undefined {
  if (!value) return `${label} is required.`;
  return value.length >= MIN_CONSOLE_PASSWORD
    ? undefined
    : `${label} must be at least ${MIN_CONSOLE_PASSWORD} characters.`;
}

interface AddForm {
  email: string;
  username: string;
  password: string;
  role: SaRole;
}

const INITIAL_ADD: AddForm = { email: '', username: '', password: '', role: 'ISSUER' };

export function ConsoleUsersPage() {
  const { user: me } = useAuth();
  const queryClient = useQueryClient();
  const usersQuery = useQuery({
    queryKey: saUserKeys.list(),
    queryFn: ({ signal }) => fetchSaUsers(signal),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: saUserKeys.all });

  // ---- add ----
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddForm>(INITIAL_ADD);
  const [addErrors, setAddErrors] = useState<Partial<Record<keyof AddForm, string>>>({});
  const addMutation = useMutation({
    mutationFn: (input: AddForm) => createSaUser(input),
    onSuccess: () => {
      refresh();
      setAddOpen(false);
    },
  });

  const openAdd = useCallback(() => {
    setAddForm(INITIAL_ADD);
    setAddErrors({});
    addMutation.reset();
    setAddOpen(true);
  }, [addMutation]);

  const handleAdd = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validation = compactErrors<keyof AddForm>({
      email: emailError(addForm.email, 'Email'),
      username: usernameError(addForm.username, 'Username'),
      password: consolePasswordError(addForm.password),
    });
    setAddErrors(validation);
    if (Object.keys(validation).length > 0) return;
    addMutation.mutate({
      ...addForm,
      email: addForm.email.trim(),
      username: addForm.username.trim(),
    });
  };

  // ---- enable / disable / role ----
  const updateMutation = useMutation({
    mutationFn: (input: { id: string; isActive?: boolean; role?: SaRole }) =>
      updateSaUser(input.id, { isActive: input.isActive, role: input.role }),
    onSuccess: refresh,
  });

  // ---- reset password ----
  const [resetTarget, setResetTarget] = useState<SaUserDto | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState<string | undefined>();
  const [resetDone, setResetDone] = useState(false);
  const resetMutation = useMutation({
    mutationFn: (input: { id: string; password: string }) =>
      resetSaUserPassword(input.id, input.password),
    onSuccess: () => {
      setResetDone(true);
      setNewPassword('');
      refresh();
    },
  });

  const openReset = (user: SaUserDto) => {
    setResetTarget(user);
    setNewPassword('');
    setResetError(undefined);
    setResetDone(false);
    resetMutation.reset();
  };

  const handleReset = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!resetTarget) return;
    const invalid = consolePasswordError(newPassword, 'New password');
    setResetError(invalid);
    if (invalid) return;
    resetMutation.mutate({ id: resetTarget.id, password: newPassword });
  };

  return (
    <>
      <PageHeader
        title="Console users"
        description="Who can sign in to this console. Issuers can only create tenants, add tills and issue licences."
        actions={
          <Button variant="primary" icon={<IconPlus className="h-4 w-4" />} onClick={openAdd}>
            Add console user
          </Button>
        }
      />

      {updateMutation.isError ? (
        <div className="mb-4">
          <Alert tone="error">{getErrorMessage(updateMutation.error)}</Alert>
        </div>
      ) : null}

      <AsyncSection
        state={usersQuery}
        skeleton={<Skeleton className="h-48 w-full" />}
        errorTitle="Could not load console users"
      >
        {(users: SaUserDto[]) => (
          <SectionCard headingId="console-users" title="Logins" count={users.length}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-surface">
                  <tr>
                    <th scope="col" className="th">Username</th>
                    <th scope="col" className="th">Email</th>
                    <th scope="col" className="th">Role</th>
                    <th scope="col" className="th">Status</th>
                    <th scope="col" className="th">Created</th>
                    <th scope="col" className="th text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((user) => {
                    const isMe = user.id === me?.id;
                    const pending = updateMutation.isPending && updateMutation.variables?.id === user.id;
                    return (
                      <tr key={user.id}>
                        <td className="td font-medium text-slate-900">
                          {user.username}
                          {isMe ? <span className="ml-1.5 text-xs text-slate-400">(you)</span> : null}
                        </td>
                        <td className="td text-slate-500">{user.email}</td>
                        <td className="td">
                          <Badge tone={user.role === 'OWNER' ? 'accent' : 'neutral'}>{ROLE_LABEL[user.role]}</Badge>
                        </td>
                        <td className="td">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Badge tone={user.isActive ? 'success' : 'neutral'} dot>
                              {user.isActive ? 'Active' : 'Disabled'}
                            </Badge>
                            {user.lockedUntil !== null ? (
                              <Badge tone="warning" dot>
                                Locked
                              </Badge>
                            ) : null}
                          </div>
                        </td>
                        <td className="td whitespace-nowrap text-slate-500">{formatDate(user.createdAt)}</td>
                        <td className="td">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" icon={<IconKey className="h-3.5 w-3.5" />} onClick={() => openReset(user)}>
                              Reset password
                            </Button>
                            {/* Your own row has no disable/demote: the API refuses
                                it too, so the console can never lose its last owner. */}
                            {!isMe ? (
                              <>
                                <Button
                                  size="sm"
                                  loading={pending && updateMutation.variables?.role !== undefined}
                                  onClick={() =>
                                    updateMutation.mutate({
                                      id: user.id,
                                      role: user.role === 'OWNER' ? 'ISSUER' : 'OWNER',
                                    })
                                  }
                                >
                                  {user.role === 'OWNER' ? 'Make issuer' : 'Make owner'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className={user.isActive ? 'text-rose-600 hover:bg-rose-50 hover:text-rose-700' : undefined}
                                  loading={pending && updateMutation.variables?.isActive !== undefined}
                                  onClick={() => updateMutation.mutate({ id: user.id, isActive: !user.isActive })}
                                >
                                  {user.isActive ? 'Disable' : 'Enable'}
                                </Button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>
        )}
      </AsyncSection>

      <Modal
        open={addOpen}
        title="Add console user"
        size="sm"
        onClose={addMutation.isPending ? () => undefined : () => setAddOpen(false)}
      >
        <form onSubmit={handleAdd} noValidate className="space-y-5">
          {addMutation.isError ? <Alert tone="error">{getErrorMessage(addMutation.error)}</Alert> : null}
          <SelectField
            id="add-role"
            label="Role"
            value={addForm.role}
            hint={ROLE_HINT[addForm.role]}
            onChange={(event) => setAddForm((f) => ({ ...f, role: event.target.value as SaRole }))}
          >
            <option value="ISSUER">{ROLE_LABEL.ISSUER}</option>
            <option value="OWNER">{ROLE_LABEL.OWNER}</option>
          </SelectField>
          <TextField
            id="add-username"
            label="Username"
            value={addForm.username}
            error={addErrors.username}
            autoComplete="off"
            onChange={(event) => setAddForm((f) => ({ ...f, username: event.target.value }))}
            data-autofocus
          />
          <TextField
            id="add-email"
            label="Email"
            type="email"
            value={addForm.email}
            error={addErrors.email}
            autoComplete="off"
            onChange={(event) => setAddForm((f) => ({ ...f, email: event.target.value }))}
          />
          <TextField
            id="add-password"
            label="Password"
            type="password"
            value={addForm.password}
            error={addErrors.password}
            hint={`At least ${MIN_CONSOLE_PASSWORD} characters. Share it over a trusted channel.`}
            autoComplete="new-password"
            onChange={(event) => setAddForm((f) => ({ ...f, password: event.target.value }))}
          />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setAddOpen(false)} disabled={addMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={addMutation.isPending}>
              Add user
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={resetTarget !== null}
        title="Reset password"
        description={resetTarget ? `For ${resetTarget.username}` : undefined}
        size="sm"
        onClose={resetMutation.isPending ? () => undefined : () => setResetTarget(null)}
      >
        {resetDone ? (
          <div className="space-y-5">
            <Alert tone="success" title="Password updated">
              Their other sessions were signed out. Share the new password over a trusted channel.
            </Alert>
            <div className="flex justify-end">
              <Button variant="primary" onClick={() => setResetTarget(null)} data-autofocus>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleReset} noValidate className="space-y-5">
            {resetMutation.isError ? <Alert tone="error">{getErrorMessage(resetMutation.error)}</Alert> : null}
            <TextField
              id="reset-console-password"
              label="New password"
              type="password"
              value={newPassword}
              error={resetError}
              hint={`At least ${MIN_CONSOLE_PASSWORD} characters.`}
              autoComplete="new-password"
              onChange={(event) => setNewPassword(event.target.value)}
              data-autofocus
            />
            <div className="flex justify-end gap-2">
              <Button onClick={() => setResetTarget(null)} disabled={resetMutation.isPending}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={resetMutation.isPending}>
                Set password
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
